/**
 * LivingDossierPanel — Phase G2 suivi (2026-09-21).
 * Back-office FR uniquement. Expose les endpoints G1 « dossier vivant » :
 *   - POST /admin/practical-sheets/{id}/attach-visit
 *   - POST /admin/practical-sheets/{id}/update-fact
 *   - POST /admin/practical-sheets/{id}/mark-item-closed
 *   - GET  /admin/practical-sheets/{id}/active-recommendations
 * Formulaires simples + timeline visits[] + liste "À recontrôler".
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../components/../ui/input";
import { Textarea } from "../ui/textarea";
import { Loader2, Anchor, Coins, DoorClosed, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function labelFactStatus(s) {
  return ({
    verified: "✔ Vérifié",
    to_recheck: "À recontrôler",
    obsolete: "Obsolète",
    closed: "Fermé",
    replaced: "Remplacé",
    temporary: "Temporaire",
  })[s] || s || "-";
}

export default function LivingDossierPanel({ sheetId, sheet }) {
  const [reloadTick, setReloadTick] = useState(0);
  const [active, setActive] = useState(null);
  const [visits, setVisits] = useState([]);

  const [visitOpen, setVisitOpen] = useState(false);
  const [factOpen, setFactOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Visit form
  const [vf, setVf] = useState({
    trip_id: "", trip_day_id: "", visit_date: "",
    cruise_company: "", ship_name: "", notes: "",
    article_id: "", report_id: "", tested_items: "",
  });
  // Fact form
  const [ff, setFf] = useState({
    key: "", label: "", new_value: "", status: "verified",
    source: "", source_url: "", verified_at: "", origin: "audit editorial",
  });
  // Closed form
  const [cf, setCf] = useState({ key: "", label: "", closed_at: "", source: "", replaced_by: "" });

  const load = useCallback(async () => {
    if (!sheetId) return;
    try {
      const { data } = await axios.get(`${API}/admin/practical-sheets/${sheetId}/active-recommendations`);
      setActive(data);
    } catch (e) { /* silencieux */ }
    setVisits(sheet?.visits || []);
  }, [sheetId, sheet, reloadTick]);

  useEffect(() => { load(); }, [load]);

  const submitVisit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...vf,
        tested_items: (vf.tested_items || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      await axios.post(`${API}/admin/practical-sheets/${sheetId}/attach-visit`, payload);
      toast.success("Visite rattachée");
      setVisitOpen(false);
      setReloadTick((t) => t + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec rattachement"); }
    finally { setBusy(false); }
  };

  const submitFact = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/practical-sheets/${sheetId}/update-fact`, ff);
      toast.success("Info mise à jour");
      setFactOpen(false);
      setReloadTick((t) => t + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setBusy(false); }
  };

  const submitClose = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/practical-sheets/${sheetId}/mark-item-closed`, cf);
      toast.success("Lieu marqué FERMÉ (conservé)");
      setCloseOpen(false);
      setReloadTick((t) => t + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setBusy(false); }
  };

  const facts = active?.active || {};
  const toRecheck = Object.entries(facts).filter(([, v]) => v?.status === "to_recheck");

  return (
    <div className="rounded-lg border border-border p-4 space-y-4" data-testid="living-dossier-panel">
      <div className="flex items-center justify-between">
        <div className="font-medium">Dossier vivant (visites terrain / infos périssables / fermetures)</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setVisitOpen((v) => !v)}
            data-testid="attach-visit-toggle">
            <Anchor className="h-4 w-4 mr-1" /> Rattacher une visite
          </Button>
          <Button size="sm" variant="outline" onClick={() => setFactOpen((v) => !v)}
            data-testid="update-fact-toggle">
            <Coins className="h-4 w-4 mr-1" /> Mettre à jour une info
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCloseOpen((v) => !v)}
            data-testid="mark-closed-toggle">
            <DoorClosed className="h-4 w-4 mr-1" /> Marquer fermé
          </Button>
        </div>
      </div>

      {visitOpen && (
        <div className="grid grid-cols-2 gap-2 p-3 border rounded">
          <Input placeholder="trip_id" value={vf.trip_id} onChange={(e) => setVf({ ...vf, trip_id: e.target.value })} data-testid="visit-trip-id" />
          <Input placeholder="trip_day_id" value={vf.trip_day_id} onChange={(e) => setVf({ ...vf, trip_day_id: e.target.value })} />
          <Input placeholder="Compagnie (MSC…)" value={vf.cruise_company} onChange={(e) => setVf({ ...vf, cruise_company: e.target.value })} />
          <Input placeholder="Navire" value={vf.ship_name} onChange={(e) => setVf({ ...vf, ship_name: e.target.value })} />
          <Input type="date" value={vf.visit_date} onChange={(e) => setVf({ ...vf, visit_date: e.target.value })} data-testid="visit-date" />
          <Input placeholder="tested_items (séparés , )" value={vf.tested_items} onChange={(e) => setVf({ ...vf, tested_items: e.target.value })} data-testid="visit-tested-items" />
          <Textarea className="col-span-2" placeholder="Notes terrain" value={vf.notes} onChange={(e) => setVf({ ...vf, notes: e.target.value })} />
          <Input placeholder="article_id (test / reportage lié)" value={vf.article_id} onChange={(e) => setVf({ ...vf, article_id: e.target.value })} />
          <Input placeholder="report_id" value={vf.report_id} onChange={(e) => setVf({ ...vf, report_id: e.target.value })} />
          <Button className="col-span-2" onClick={submitVisit} disabled={busy} data-testid="visit-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Rattacher
          </Button>
        </div>
      )}

      {factOpen && (
        <div className="grid grid-cols-2 gap-2 p-3 border rounded">
          <Input placeholder="key (ex: shuttle_price)" value={ff.key} onChange={(e) => setFf({ ...ff, key: e.target.value })} data-testid="fact-key" />
          <Input placeholder="label (ex: Navette port-centre)" value={ff.label} onChange={(e) => setFf({ ...ff, label: e.target.value })} />
          <Input placeholder="Nouvelle valeur" value={ff.new_value} onChange={(e) => setFf({ ...ff, new_value: e.target.value })} data-testid="fact-value" />
          <select className="border rounded px-2 h-9 text-sm" value={ff.status} onChange={(e) => setFf({ ...ff, status: e.target.value })}>
            <option value="verified">Vérifié</option>
            <option value="to_recheck">À recontrôler</option>
            <option value="temporary">Temporaire</option>
            <option value="obsolete">Obsolète</option>
            <option value="replaced">Remplacé</option>
          </select>
          <Input placeholder="Source" value={ff.source} onChange={(e) => setFf({ ...ff, source: e.target.value })} />
          <Input placeholder="Source URL" value={ff.source_url} onChange={(e) => setFf({ ...ff, source_url: e.target.value })} />
          <Input type="date" placeholder="Vérifié le (jamais inventé)" value={ff.verified_at} onChange={(e) => setFf({ ...ff, verified_at: e.target.value })} data-testid="fact-verified-at" />
          <Input placeholder="Origine (visite terrain, audit…)" value={ff.origin} onChange={(e) => setFf({ ...ff, origin: e.target.value })} />
          <Button className="col-span-2" onClick={submitFact} disabled={busy} data-testid="fact-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Enregistrer (ancienne valeur conservée dans l'historique)
          </Button>
        </div>
      )}

      {closeOpen && (
        <div className="grid grid-cols-2 gap-2 p-3 border rounded">
          <Input placeholder="key" value={cf.key} onChange={(e) => setCf({ ...cf, key: e.target.value })} data-testid="close-key" />
          <Input placeholder="label" value={cf.label} onChange={(e) => setCf({ ...cf, label: e.target.value })} />
          <Input type="date" value={cf.closed_at} onChange={(e) => setCf({ ...cf, closed_at: e.target.value })} />
          <Input placeholder="Source" value={cf.source} onChange={(e) => setCf({ ...cf, source: e.target.value })} />
          <Input placeholder="Remplacé par (facultatif)" value={cf.replaced_by} onChange={(e) => setCf({ ...cf, replaced_by: e.target.value })} />
          <Button className="col-span-2" onClick={submitClose} disabled={busy} data-testid="close-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Marquer fermé (jamais supprimé)
          </Button>
        </div>
      )}

      {/* Timeline visits */}
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Chronologie visites</div>
        {visits.length === 0 ? (
          <div className="text-xs text-muted-foreground">Aucune visite rattachée.</div>
        ) : (
          <ol className="border-l-2 border-border pl-3 space-y-2" data-testid="visits-timeline">
            {visits.map((v, i) => (
              <li key={i} className="text-xs" data-testid={`visit-${i}`}>
                <span className="font-medium">{v.visit_date || v.attached_at?.slice(0, 10) || "—"}</span>
                {" · "}
                {v.cruise_company || "—"} {v.ship_name || ""}
                {v.notes ? <> — <span className="text-muted-foreground">{v.notes}</span></> : null}
                {v.article_id ? <> · article: {v.article_id.slice(0, 8)}…</> : null}
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Infos à recontrôler */}
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> À recontrôler
        </div>
        {toRecheck.length === 0 ? (
          <div className="text-xs text-muted-foreground">Rien à recontrôler.</div>
        ) : (
          <ul className="text-xs space-y-1" data-testid="to-recheck-list">
            {toRecheck.map(([k, v]) => (
              <li key={k}>
                <span className="font-medium">{v.label || k}</span> —
                <span className="text-muted-foreground"> {labelFactStatus(v.status)}</span>
                {v.last_verified_at ? <> · dernière vérif : {v.last_verified_at.slice(0, 10)}</> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
